import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useLang, scrollToId } from "./ctx";
import { CONTACT } from "../../content";

const IDS = ["about", "kaolin", "process", "contact"];

const LangToggle = ({ testid }) => {
  const { lang, setLang } = useLang();
  return (
    <button
      data-testid={testid}
      onClick={() => setLang(lang === "tr" ? "en" : "tr")}
      className="relative flex items-center rounded-full border border-stone-300 p-0.5 font-mono text-[11px] tracking-widest"
    >
      {["tr", "en"].map((l) => (
        <span key={l} className={`relative z-10 px-2.5 py-1 transition-colors duration-300 ${lang === l ? "text-white" : "text-stone-500"}`}>
          {lang === l && <motion.span layoutId={`pill-${testid}`} className="absolute inset-0 -z-10 rounded-full bg-[#14120F]" transition={{ type: "spring", stiffness: 400, damping: 32 }} />}
          {l.toUpperCase()}
        </span>
      ))}
    </button>
  );
};

export const Nav = () => {
  const { t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 40);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);
  useEffect(() => {
    if (!open) return undefined;
    window.__lenis?.stop();
    return () => window.__lenis?.start();
  }, [open]);
  const go = (id) => { setOpen(false); setTimeout(() => scrollToId(id), 50); };

  return (
    <>
    <header className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,padding] duration-500 ${scrolled || open ? "border-b border-stone-200/70 bg-[#FBFAF7]/90 py-3 backdrop-blur-xl" : "border-b border-transparent py-4 sm:py-6"}`}>
      <nav className="mx-auto flex max-w-[1440px] items-center justify-between px-6 lg:px-12">
        <button data-testid="nav-brand-logo" onClick={() => go("top")} className="shrink-0">
          <img src="/assets/logo-dark.png" alt="Balgat Mining" className={`w-auto transition-[height] duration-500 ${scrolled || open ? "h-9" : "h-10 sm:h-11"}`} />
        </button>
        <div className="hidden items-center gap-9 lg:flex">
          {IDS.map((id) => (
            <button key={id} data-testid={`nav-link-${id}`} onClick={() => go(id)} className="group relative text-[13px] tracking-wide text-stone-700 hover:text-stone-950">
              {t.nav[id]}
              <span className="absolute -bottom-1 left-0 h-px w-full origin-left scale-x-0 bg-[#A8874F] transition-transform duration-500 group-hover:scale-x-100" />
            </button>
          ))}
        </div>
        <div className="flex items-center gap-3">
          <LangToggle testid="language-toggle-button" />
          <button data-testid="nav-cta-button" onClick={() => go("contact")} className="hidden rounded-full bg-[#14120F] px-5 py-2.5 text-[12px] tracking-wide text-white transition-colors duration-300 hover:bg-[#A8874F] md:block">
            {t.nav.cta}
          </button>
          <button data-testid="mobile-menu-button" onClick={() => setOpen((o) => !o)} className="rounded-full border border-stone-300 p-2 lg:hidden" aria-label="menu">
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </nav>
    </header>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ clipPath: "inset(0 0 100% 0)" }} animate={{ clipPath: "inset(0 0 0% 0)" }} exit={{ clipPath: "inset(0 0 100% 0)" }}
            transition={{ duration: 0.6, ease: [0.76, 0, 0.24, 1] }}
            className="fixed inset-0 z-[45] flex flex-col justify-between overflow-y-auto bg-[#FBFAF7] px-6 pb-10 pt-24 lg:hidden" data-testid="mobile-menu"
          >
            <div>
              {IDS.map((id, i) => (
                <motion.button
                  key={id} data-testid={`mobile-nav-link-${id}`} onClick={() => go(id)}
                  initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 + i * 0.07, duration: 0.6 }}
                  className="flex w-full items-baseline gap-4 border-b border-stone-200 py-5 text-left"
                >
                  <span className="font-mono text-[11px] text-[#A8874F]">0{i + 1}</span>
                  <span className="font-display text-3xl font-light text-stone-950">{t.nav[id]}</span>
                </motion.button>
              ))}
            </div>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }} className="space-y-2 text-sm text-stone-600">
              <a href={CONTACT.phoneHref} data-testid="mobile-menu-phone" className="block">{CONTACT.phone}</a>
              <a href={`mailto:${CONTACT.email}`} data-testid="mobile-menu-email" className="block">{CONTACT.email}</a>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
